/**
 * OpenAI API service
 * Handles image analysis using GPT Vision
 */

const OPENAI_BASE_URL = 'https://api.openai.com/v1';

/**
 * Analyze an image using OpenAI Vision
 * @param {Object} params
 * @param {string} params.apiKey - OpenAI API key
 * @param {string} params.model - Model ID (e.g., 'gpt-4o')
 * @param {string} params.prompt - Analysis prompt
 * @param {string} params.imageBase64 - Base64 encoded image
 * @returns {Promise<string>} - Analysis text
 */
export const analyzeImage = async ({ apiKey, model, prompt, imageBase64 }) => {
  const response = await fetch(`${OPENAI_BASE_URL}/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: model,
      messages: [{
        role: 'user',
        content: [
          { type: 'text', text: prompt },
          {
            type: 'image_url',
            image_url: {
              url: `data:image/jpeg;base64,${imageBase64}`,
              detail: 'high'
            }
          }
        ]
      }],
      max_completion_tokens: 1000
    })
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error?.message || 'Failed to analyze image');
  }

  const data = await response.json();
  return data.choices?.[0]?.message?.content || 'No analysis available';
};

/**
 * Analyze multiple images in sequence
 * @param {Object} params
 * @param {string} params.apiKey - OpenAI API key
 * @param {string} params.model - Model ID
 * @param {string} params.prompt - Analysis prompt
 * @param {Array<{id: string, base64: string, preview: string, fileName: string}>} params.images
 * @returns {Promise<Array<{id: string, preview: string, fileName: string, analysis: string}>>}
 */
export const analyzeMultipleImages = async ({ apiKey, model, prompt, images }) => {
  const results = [];
  
  for (const img of images) {
    const analysis = await analyzeImage({
      apiKey,
      model,
      prompt,
      imageBase64: img.base64
    });
    
    results.push({
      id: img.id,
      preview: img.preview,
      fileName: img.fileName,
      analysis
    });
  }
  
  return results;
};

/**
 * Generate an image-to-prompt using OpenAI GPT
 * Analyzes an image and generates a prompt for image/video generation
 * @param {string} apiKey - OpenAI API key
 * @param {string} model - Model ID (e.g., 'gpt-5.2')
 * @param {string} imageBase64 - Base64 encoded image (with or without data URL prefix)
 * @param {string} userInstruction - Optional user instruction for customization
 * @param {string} systemPrompt - Complete system prompt for the selected profile
 * @returns {Promise<string>} - Generated prompt text
 */
export const generateImagePromptOpenAI = async (apiKey, model, imageBase64, userInstruction, systemPrompt) => {
  // Build the user message with just the task instruction
  const userMessage = 'TASK:\n' + (userInstruction || 'Generate a detailed prompt based on this image.');
  
  // Extract base64 data (remove data URL prefix if present)
  const base64Data = imageBase64.includes(',') ? imageBase64.split(',')[1] : imageBase64;
  
  const response = await fetch(`${OPENAI_BASE_URL}/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: model,
      messages: [
        {
          role: 'system',
          content: systemPrompt
        },
        {
          role: 'user',
          content: [
            { type: 'text', text: userMessage },
            {
              type: 'image_url',
              image_url: {
                url: `data:image/jpeg;base64,${base64Data}`,
                detail: 'high'
              }
            }
          ]
        }
      ],
      max_completion_tokens: 2048,
      temperature: 0.7
    })
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error?.message || `OpenAI API error: ${response.statusText}`);
  }

  const data = await response.json();
  
  const content = data.choices?.[0]?.message?.content;
  if (content) {
    return content;
  }
  
  throw new Error('No text response from API');
};

/**
 * Generate a prompt from text description (no image) using OpenAI
 * @param {string} apiKey - OpenAI API key
 * @param {string} model - Model ID (e.g., 'gpt-5.2')
 * @param {string} textDescription - User's text description
 * @param {string} userInstruction - Optional user instruction
 * @param {string} systemPrompt - Complete system prompt for the selected profile
 * @returns {Promise<string>} - Generated prompt text
 */
export const generateTextOnlyPromptOpenAI = async (apiKey, model, textDescription, userInstruction, systemPrompt) => {
  // Build the user message
  const userMessage = `DESCRIPTION:\n${textDescription}\n\n${
    userInstruction ? `ADDITIONAL INSTRUCTIONS:\n${userInstruction}\n\n` : ''
  }TASK:\nGenerate a detailed, high-quality prompt for image generation based on the above description.`;
  
  const response = await fetch(`${OPENAI_BASE_URL}/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: model,
      messages: [
        {
          role: 'system',
          content: systemPrompt
        },
        {
          role: 'user',
          content: userMessage
        }
      ],
      max_completion_tokens: 2048,
      temperature: 0.7
    })
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error?.message || `OpenAI API error: ${response.statusText}`);
  }

  const data = await response.json();
  
  const content = data.choices?.[0]?.message?.content;
  if (content) {
    return content;
  }
  
  throw new Error('No text response from API');
};

/**
 * Generate a prompt from multiple images using OpenAI
 * Analyzes multiple images and generates a combined prompt (e.g., for transitions)
 * @param {Object} params
 * @param {string} params.apiKey - OpenAI API key
 * @param {string} params.model - Model ID (e.g., 'gpt-5.2')
 * @param {Array<string>} params.images - Array of base64 encoded images
 * @param {string} params.userMessage - User message/instruction
 * @param {string} params.systemPrompt - System prompt
 * @returns {Promise<string>} - Generated prompt text
 */
export const generateMultiImagePromptOpenAI = async ({ apiKey, model, images, userMessage, systemPrompt }) => {
  if (!images || images.length === 0) {
    throw new Error('At least one image is required');
  }
  
  // Build image content array
  const imageContent = images.map(imageBase64 => {
    const base64Data = imageBase64 && imageBase64.includes ? 
      (imageBase64.includes(',') ? imageBase64.split(',')[1] : imageBase64) : 
      imageBase64;
    
    return {
      type: 'image_url',
      image_url: {
        url: `data:image/jpeg;base64,${base64Data}`,
        detail: 'high'
      }
    };
  });
  
  const response = await fetch(`${OPENAI_BASE_URL}/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: model,
      messages: [
        {
          role: 'system',
          content: systemPrompt
        },
        {
          role: 'user',
          content: [
            ...imageContent,
            { type: 'text', text: userMessage }
          ]
        }
      ],
      max_completion_tokens: 2048,
      temperature: 0.7
    })
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error?.message || `OpenAI API error: ${response.statusText}`);
  }

  const data = await response.json();
  
  const content = data.choices?.[0]?.message?.content;
  if (content) {
    return content;
  }
  
  throw new Error('No text response from API');
};
